import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import BeltBadge from '../../components/common/BeltBadge';
import QuestPanel from '../../components/common/QuestPanel';
import WarriorSetupModal from '../../components/common/WarriorSetupModal';
import useAuth from '../../hooks/useAuth';
import { ktmThisMonth } from '../../utils/calendar';
import { useTenant } from '../../context/TenantContext';
import { hasClasses } from '../../utils/publicProfile';
import { getClasses } from '../../services/classService';
import { getMyFees } from '../../services/feeService';
import { getStudentMonthlyAttendance } from '../../services/attendanceService';
import { getMyGamification } from '../../services/gamificationService';
import { MdPayments, MdEmojiEvents, MdCheckCircle, MdSchedule, MdMilitaryTech, MdLeaderboard } from 'react-icons/md';
import api from '../../services/api';
import { FEE_STATUS_BADGE, FEE_STATUS_LABEL } from '../../utils/feeStatus';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const AVATARS = ['🥷','🐉','🦅','🐯','⚡','🔥','🦁','🐺','🦊','🐻','🦸','🌟'];
const getAvatar = (style) => AVATARS[(style || 1) - 1] || '🥷';

const LEAGUE_CONFIG = {
  Bronze:   { icon: '🥉', color: 'text-amber-600'   },
  Silver:   { icon: '🥈', color: 'text-gray-500'     },
  Gold:     { icon: '🥇', color: 'text-yellow-500'   },
  Sapphire: { icon: '💎', color: 'text-blue-600'     },
  Ruby:     { icon: '💎', color: 'text-red-600'      },
  Diamond:  { icon: '💎', color: 'text-purple-600'   },
};

// Fallback level title for cached/old API responses without levelName
const LEVEL_TITLE = (l) => {
  if (l >= 9)  return 'Elite Student';
  if (l >= 8)  return 'Team Leader';
  if (l >= 7)  return 'Belt Chaser';
  if (l >= 6)  return 'Dojang Regular';
  if (l >= 5)  return 'Focused Student';
  if (l >= 4)  return 'Rising Fighter';
  if (l >= 3)  return 'Disciplined Learner';
  if (l >= 2)  return 'White Warrior';
  return 'Beginner';
};

const GRADING_STYLES = {
  not_ready:    { color: 'text-gray-500',  bg: 'bg-gray-50',   border: 'border-gray-200',   icon: '🎯' },
  getting_close:{ color: 'text-orange-600',bg: 'bg-orange-50', border: 'border-orange-200', icon: '🔥' },
  ready:        { color: 'text-green-600', bg: 'bg-green-50',  border: 'border-green-200',  icon: '✅' },
  advanced:     { color: 'text-purple-600',bg: 'bg-purple-50', border: 'border-purple-200', icon: '🥋' },
};

const streakMotivation = (streak) => {
  if (streak === 0)  return "Suit up. Your legend starts today.";
  if (streak < 3)    return `${streak}-session streak! Every warrior starts somewhere. 🔥`;
  if (streak < 7)    return `${streak} sessions deep. The habit is forming. Keep the fire!`;
  if (streak < 14)   return `🔥 ${streak} straight! One week of war. You're unstoppable!`;
  if (streak < 30)   return `🔥🔥 ${streak} sessions! Two weeks of pure dedication. Elite.`;
  return `🚀 ${streak}-session legend. You belong in the Diamond League.`;
};

const useCountdown = (targetTime) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    if (!targetTime) return;
    const tick = () => {
      const diff = targetTime - new Date();
      if (diff <= 0) { setRemaining('Started'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetTime]);
  return remaining;
};

function AttendanceCircle({ percentage }) {
  const r    = 28;
  const circ = 2 * Math.PI * r;
  const dash = (percentage / 100) * circ;
  const color = percentage >= 75 ? '#22C55E' : percentage >= 50 ? '#F97316' : '#EF4444';
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#F3F4F6" strokeWidth="6" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-secondary">{percentage}%</span>
      </div>
    </div>
  );
}

function StudentDashboard() {
  document.title = 'Dashboard — TrainAxis';
  const { user }                    = useAuth();
  const { club }                    = useTenant();
  // Open-floor gym members have no class schedule or attendance — hide those widgets. Fee status
  // and everything else stay. Reversible, no data touched.
  const showClasses                 = hasClasses(club?.clubType);
  const [showWarrior, setWarrior]   = useState(false);

  const { data: profileData, isPending: loadingProfile } = useQuery({
    queryKey: ['student-me'],
    queryFn:  () => api.get('/api/students/me').then(r => r.data),
  });

  const profile       = profileData?.profile;
  const userInfo      = profile?.user;
  const belt          = profile?.currentGrade || 'White';
  const hasWarrior    = userInfo?.hasChosenWarrior;
  const warriorTitle  = userInfo?.warriorTitle;
  const avatarStyle   = userInfo?.avatarStyle || 1;
  const firstName     = user?.name?.split(' ')[0] || user?.name || '';
  const displayName   = warriorTitle ? `${firstName} ${warriorTitle}` : firstName;

  // Trigger warrior setup if not chosen yet
  useEffect(() => {
    if (profile && hasWarrior === false && !showWarrior) {
      const timer = setTimeout(() => setWarrior(true), 800);
      return () => clearTimeout(timer);
    }
  }, [profile, hasWarrior]);

  const { data: classData } = useQuery({
    queryKey: ['classes'],
    queryFn:  () => getClasses({ isActive: 'true' }).then(r => r.data),
  });

  const { data: feesData } = useQuery({
    queryKey: ['my-fees'],
    queryFn:  () => getMyFees().then(r => r.data),
  });

  const { data: attData } = useQuery({
    queryKey: ['my-attendance', profile?._id],
    queryFn:  () => getStudentMonthlyAttendance(profile._id, ktmThisMonth()).then(r => r.data),
    enabled:  !!profile?._id,
  });

  const { data: gamData } = useQuery({
    queryKey: ['my-gamification'],
    queryFn:  () => getMyGamification().then(r => r.data),
    refetchInterval: 60000,
  });

  const g           = gamData?.gamification;
  const xpInLevel   = g?.xpInLevel ?? 0;
  const xpForLevel  = g?.xpForLevel || 1000;
  const xpPct       = Math.min(100, Math.round((xpInLevel / xpForLevel) * 100));
  const levelName   = g?.levelName || LEVEL_TITLE(g?.level ?? 1);
  const leagueConf  = LEAGUE_CONFIG[g?.currentLeague || 'Bronze'];

  // Next class
  const classes   = classData?.classes || [];
  const todayDay  = DAYS[new Date().getDay()];
  const nowMins   = new Date().getHours() * 60 + new Date().getMinutes();

  const upcomingClass = classes
    .filter(c => c.days?.includes(todayDay) && c.discipline === (profile?.discipline || 'Taekwondo'))
    .sort((a, b) => {
      const [ah, am] = (a.startTime || '99:99').split(':').map(Number);
      const [bh, bm] = (b.startTime || '99:99').split(':').map(Number);
      return (ah * 60 + am) - (bh * 60 + bm);
    })
    .find(c => {
      if (!c.startTime) return false;
      const [h, m] = c.startTime.split(':').map(Number);
      return (h * 60 + m) > nowMins;
    });

  const nextClassTime = upcomingClass?.startTime ? (() => {
    const [h, m] = upcomingClass.startTime.split(':').map(Number);
    const t = new Date(); t.setHours(h, m, 0, 0); return t;
  })() : null;

  const countdown = useCountdown(nextClassTime);

  const thisMonthPay = feesData?.currentFee || null;

  const FEE_STYLE = FEE_STATUS_BADGE;
  const FEE_LABEL = FEE_STATUS_LABEL;

  return (
    <div>
      {/* ══════════════════════════════════════════════════════
          HERO BANNER — the "video game opening screen"
      ══════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-br from-gray-900 via-red-950 to-gray-900 rounded-3xl p-5 mb-6 overflow-hidden relative">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #ef4444 0%, transparent 50%), radial-gradient(circle at 80% 20%, #f97316 0%, transparent 40%)' }} />

        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {/* Left — Avatar + name + belt */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-red-700 rounded-2xl flex items-center justify-center text-4xl shadow-xl shadow-primary/30">
                {getAvatar(avatarStyle)}
              </div>
              {/* Level badge */}
              {g && (
                <div className="absolute -bottom-1.5 -right-1.5 bg-primary text-white text-[10px] font-black px-1.5 py-0.5 rounded-full shadow">
                  {g.level}
                </div>
              )}
            </div>
            <div>
              <p className="text-white/60 text-xs">Welcome back</p>
              <p className="text-white font-black text-lg leading-tight">{displayName}</p>
              <div className="mt-1">
                <BeltBadge belt={belt} size="xs" />
              </div>
            </div>
          </div>

          {/* Middle — Streak (HUGE) */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 sm:border-x sm:border-white/10 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-5xl leading-none" style={{ filter: 'drop-shadow(0 0 12px rgba(249,115,22,0.8))' }}>🔥</span>
              <div>
                <p className="text-5xl font-black text-white leading-none">{g?.streak ?? 0}</p>
                <p className="text-white/60 text-xs">day streak</p>
              </div>
            </div>
            <p className="text-white/60 text-xs mt-2 text-center italic leading-tight max-w-xs">
              {streakMotivation(g?.streak ?? 0)}
            </p>
            {g?.streakFreezes > 0 && (
              <div className="flex items-center gap-1 mt-2 bg-white/10 rounded-xl px-2 py-1">
                <span className="text-sm">🛡️</span>
                <span className="text-white/70 text-xs">{g.streakFreezes} freeze{g.streakFreezes !== 1 ? 's' : ''} left</span>
              </div>
            )}
          </div>

          {/* Right — League + XP */}
          <div className="flex-shrink-0 min-w-0 sm:text-right">
            {/* League */}
            {g && (
              <div className="flex sm:justify-end items-center gap-1.5 mb-2">
                <span className="text-xl">{leagueConf.icon}</span>
                <span className={`text-sm font-bold ${leagueConf.color}`}>{g.currentLeague} League</span>
              </div>
            )}
            {/* XP bar */}
            {g && (
              <>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white/60 text-xs">{levelName}</span>
                  <span className="text-white/80 text-xs font-semibold">{xpInLevel}/{xpForLevel}</span>
                </div>
                <div className="w-full sm:w-40 bg-white/10 rounded-full h-2">
                  <div className="h-2 rounded-full bg-gradient-to-r from-primary to-orange-400 transition-all duration-1000" style={{ width: `${xpPct}%` }} />
                </div>
                {g.isMaxLevel
                  ? <p className="text-white/40 text-[10px] mt-1">Maximum level reached 👑</p>
                  : <p className="text-white/40 text-[10px] mt-1">{g.xpToNextLevel} XP → {g.nextLevelName || `Lv.${g.level + 1}`}</p>
                }
              </>
            )}
            <Link to="/student/leaderboard" className="text-primary text-xs font-semibold hover:text-red-400 transition-colors mt-1.5 block">
              {g?.currentLeague === 'Diamond' ? 'Push to the top →' : 'View Leaderboard →'}
            </Link>
          </div>
        </div>
      </div>

      {/* ── Quest Panel ─────────────────────────────────── */}
      <Card className="mb-6">
        <QuestPanel />
      </Card>

      {/* ── Today's Class + Fee status ──────────────────── */}
      <div className={`grid grid-cols-1 ${showClasses ? 'sm:grid-cols-2' : ''} gap-4 mb-6`}>
        {/* Next Class — hidden for open-floor gym members */}
        {showClasses && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted font-medium">Next Class Today</p>
              {upcomingClass ? (
                <>
                  <p className="font-bold text-secondary text-sm mt-1">{upcomingClass.name}</p>
                  <p className="text-primary text-xl font-black">{countdown}</p>
                </>
              ) : (
                <p className="text-muted text-sm mt-1">No more classes today 🏠</p>
              )}
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center flex-shrink-0">
              <MdSchedule className="text-2xl text-green-600" />
            </div>
          </div>
        </Card>
        )}

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted font-medium">Fee Status</p>
              {thisMonthPay ? (
                <>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${FEE_STYLE[thisMonthPay.status] || FEE_STYLE.pending}`}>
                    {FEE_LABEL[thisMonthPay.status] || thisMonthPay.status}
                  </span>
                  <p className="text-secondary font-bold text-lg mt-0.5">NPR {thisMonthPay.amount?.toLocaleString()}</p>
                </>
              ) : (
                <p className="text-muted text-sm mt-1">No record this month</p>
              )}
            </div>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${thisMonthPay?.status === 'completed' ? 'bg-green-50' : thisMonthPay?.status === 'failed' ? 'bg-red-50' : 'bg-yellow-50'}`}>
              <MdPayments className={`text-2xl ${thisMonthPay?.status === 'completed' ? 'text-green-600' : thisMonthPay?.status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`} />
            </div>
          </div>
          <Link to="/student/fees" className="text-xs text-primary hover:underline font-medium mt-2 block">
            View details →
          </Link>
        </Card>
      </div>

      {/* ── Attendance circle — hidden for open-floor gym members ── */}
      {showClasses && (
      <Card className="flex items-center gap-4 mb-6">
        <AttendanceCircle percentage={attData?.percentage ?? 0} />
        <div>
          <p className="text-xs text-muted font-medium">Attendance This Month</p>
          <p className="font-bold text-secondary text-xl">{attData?.present ?? 0} / {attData?.total ?? 0}</p>
          <p className="text-xs text-muted">sessions present</p>
        </div>
        <Link to="/student/attendance" className="ml-auto text-xs text-primary hover:underline font-medium">
          View →
        </Link>
      </Card>
      )}

      {/* ── Grading Readiness ───────────────────────────── */}
      {g?.gradingReadiness && g.gradingReadiness.status !== 'advanced' && (() => {
        const gr  = g.gradingReadiness;
        const sty = GRADING_STYLES[gr.status] || GRADING_STYLES.not_ready;
        return (
          <Card className={`flex items-center gap-4 mb-6 border ${sty.border} ${sty.bg}`}>
            <span className="text-3xl flex-shrink-0">{sty.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${sty.color}`}>
                {gr.status === 'ready' ? 'Grading Ready' : gr.status === 'getting_close' ? 'Getting Close' : 'Keep Training'}
              </p>
              <p className="text-xs text-muted leading-snug">{gr.message}</p>
              {g.nextBelt && gr.status !== 'not_ready' && (
                <p className="text-[10px] text-muted mt-0.5">Next target: <span className="font-semibold">{g.nextBelt}</span></p>
              )}
            </div>
            <Link to="/student/belt-progress" className={`text-xs font-semibold flex-shrink-0 ${sty.color}`}>
              →
            </Link>
          </Card>
        );
      })()}

      {/* ── Recent Achievements ─────────────────────────── */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-secondary flex items-center gap-2">
            <MdMilitaryTech className="text-primary" /> Recent Achievements
          </p>
          <Link to="/student/badges" className="text-xs text-primary hover:underline">View all →</Link>
        </div>
        {g?.recentBadges?.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {g.recentBadges.map(b => (
              <div key={b.badgeId} className="flex flex-col items-center gap-1 bg-card border border-divider rounded-2xl px-4 py-3 shadow-sm flex-shrink-0">
                <span className="text-3xl">{b.icon}</span>
                <p className="text-xs font-semibold text-secondary text-center max-w-[72px] leading-tight">{b.name}</p>
                <p className="text-[10px] text-muted capitalize">{b.rarity}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-nav-hover border border-divider rounded-2xl px-4 py-5 text-center">
            <span className="text-3xl">🏅</span>
            <p className="text-xs text-muted font-medium mt-2">No achievements yet</p>
            <p className="text-[11px] text-muted mt-0.5">Your achievements will appear here after your first class.</p>
          </div>
        )}
      </div>

      {/* ── Quick links ─────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          // Attendance quick link — hidden for open-floor gym members.
          ...(showClasses ? [{ to: '/student/attendance',    label: 'Attendance',   Icon: MdCheckCircle,  bg: 'bg-blue-50',   text: 'text-blue-600'   }] : []),
          { to: '/student/belt-progress', label: 'Belt Progress', Icon: MdEmojiEvents,  bg: 'bg-yellow-50', text: 'text-yellow-600' },
          { to: '/student/leaderboard',   label: 'Leaderboard',  Icon: MdLeaderboard,  bg: 'bg-orange-50', text: 'text-orange-600' },
          { to: '/student/badges',        label: 'My Badges',    Icon: MdMilitaryTech, bg: 'bg-purple-50', text: 'text-purple-600' },
        ].map(({ to, label, Icon, bg, text }) => (
          <Link key={to} to={to}>
            <Card className="flex flex-col items-center py-4 gap-2 text-center transition-colors duration-150 hover:border-secondary cursor-pointer">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${bg}`}>
                <Icon className={`text-xl ${text}`} />
              </div>
              <p className="text-xs font-medium text-secondary">{label}</p>
            </Card>
          </Link>
        ))}
      </div>

      {/* ── Warrior setup modal ─────────────────────────── */}
      {showWarrior && (
        <WarriorSetupModal
          userName={user?.name}
          onComplete={() => setWarrior(false)}
        />
      )}
    </div>
  );
}

export default StudentDashboard;
